import React from 'react';
import PropTypes from 'prop-types';
import { flatten, map, uniq, includes } from 'lodash';
import { compose, withPropsOnChange, withStateHandlers } from 'recompose';
import classnames from 'classnames';

import { Dropdown } from 'components/core/Dropdown';
import { H3, Paragraph, Text } from 'components/core/Typography';
import { BookableUnitDetails } from 'components/unit/BookableUnitDetails';
import { Link } from 'components/core/Link';
import { Button } from 'components/core/Button';

import './StorageUnits.scss';

export const enhance = compose(
	withStateHandlers(
		{ selectedSize: null, selectedAmenity: null },
		{
			onSelectSize: () => (selectedSize) => ({ selectedSize }),
			onSelectAmenity: () => (selectedAmenity) => ({ selectedAmenity }),
		},
	),
	withPropsOnChange(['units'], ({ units }) => ({
		sizes: uniq(map(units, 'sizeBucket')),
		amenities: uniq(flatten(map(units, 'availableAmenities'))).sort(),
	})),
	withPropsOnChange(['units', 'selectedSize'], ({ selectedSize, units }) => ({
		units: selectedSize
			? units.filter((unit) => unit.sizeBucket === selectedSize)
			: units,
	})),
	withPropsOnChange(
		['units', 'selectedAmenity'],
		({ selectedAmenity, units }) => ({
			units: selectedAmenity
				? units.filter((unit) =>
						includes(unit.availableAmenities, selectedAmenity),
					)
				: units,
		}),
	),
);

export function StorageUnits({
	expandedUnit,
	facility,
	onCreateBooking,
	onExpandUnit,
	storageType,
	units,
	sizes,
	amenities,
	selectedSize,
	selectedAmenity,
	onSelectSize,
	onSelectAmenity,
	onShowUnitSizeModal,
	onShowUnitAmenityModal,
	isNewSearchPage,
}) {
	return (
		<div className="storage-units">
			<div
				className={classnames('unit-list-header', {
					'show-fees': facility.showFeeWarning,
				})}
			>
				<H3
					size="medium"
					className="headline"
				>
					Available Units
				</H3>
				<div className="unit-list-filters">
					<Dropdown
						label="Unit Sizes"
						options={sizes}
						selected={selectedSize}
						onSelect={onSelectSize}
					/>
					<Dropdown
						label="Amenities"
						options={amenities}
						selected={selectedAmenity}
						onSelect={onSelectAmenity}
					/>
				</div>
				<div className="unit-guides">
					Not sure what{' '}
					<Button
						link
						onClick={onShowUnitSizeModal}
						className="unit-size-modal-button"
					>
						<Text>unit size</Text>
					</Button>{' '}
					you'll need? Need help determining what{' '}
					<Button
						link
						onClick={onShowUnitAmenityModal}
						className="unit-size-modal-button"
					>
						<Text>amenities</Text>
					</Button>{' '}
					are right for you?{' '}
					<Link
						href="/what-to-expect/"
						target="_blank"
					>
						We can help!
					</Link>
				</div>
			</div>
			{facility.showFeeWarning && facility.totalFees > 0 && (
				<Paragraph
					last
					className="fees-info-banner"
					color="white"
				>
					Select a unit to see full pricing with additional fees
					displayed.
				</Paragraph>
			)}
			<div className="unit-card-list">
				{units.map((unit) => (
					<BookableUnitDetails
						key={unit.id}
						unit={unit}
						facility={facility}
						onExpandUnit={onExpandUnit}
						onCreateBooking={onCreateBooking}
						isExpanded={expandedUnit === unit.id}
						storageType={storageType}
						isNewSearchPage={isNewSearchPage}
					/>
				))}
			</div>

			{!units.length && (
				<div className="no-units">
					<Paragraph>
						No unit matched the selected criteria.
					</Paragraph>
				</div>
			)}
		</div>
	);
}

StorageUnits.propTypes = {
	amenities: PropTypes.arrayOf(PropTypes.string).isRequired,
	expandedUnit: PropTypes.number,
	facility: PropTypes.object.isRequired,
	onCreateBooking: PropTypes.func.isRequired,
	onExpandUnit: PropTypes.func.isRequired,
	onSelectAmenity: PropTypes.func.isRequired,
	onSelectSize: PropTypes.func.isRequired,
	selectedAmenity: PropTypes.string,
	selectedSize: PropTypes.string,
	sizes: PropTypes.arrayOf(PropTypes.string).isRequired,
	storageType: PropTypes.string,
	units: PropTypes.arrayOf(PropTypes.object).isRequired,
	onShowUnitSizeModal: PropTypes.func,
	onShowUnitAmenityModal: PropTypes.func,
	isNewSearchPage: PropTypes.bool,
};

export default enhance(StorageUnits);
